import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { usePricing } from '../hooks/usePricing';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Clock, Inbox } from 'lucide-react';

/** Budget label from a request's optional min/max (mirrors ServiceRequestDetail). */
const budgetLabel = (request, t) => {
  const { budget_min: min, budget_max: max } = request;
  if (min != null && max != null) {
    return t('services.reqBudgetRange', { min: Number(min).toLocaleString(), max: Number(max).toLocaleString() });
  }
  if (min != null) return t('services.reqBudgetFrom', { amount: Number(min).toLocaleString() });
  if (max != null) return t('services.reqBudgetUpTo', { amount: Number(max).toLocaleString() });
  return t('services.reqBudgetOpen');
};

/** Human "expires in N days" from an ISO timestamp. */
const expiresLabel = (expiresAt, t) => {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return t('services.reqExpired');
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return t('services.reqExpiresToday');
  return t('services.reqExpiresIn', { count: days });
};

/**
 * Pro dashboard "Requests for you" section (mirrors MyServiceListingsSection). Fetches
 * GET /api/service-requests/available and lists request cards linking to the detail page.
 *
 * Renders null when the flag is off, the Pro has no active listing (checked once via
 * GET /api/pro-listings/mine), or the fetch errored — a network hiccup must self-hide
 * rather than show a false "no matching requests" empty state.
 */
const MatchingRequestsSection = ({ profile }) => {
  const { t } = useTranslation();
  const { pricing } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [requests, setRequests] = useState([]);
  const [hasActiveListing, setHasActiveListing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchData = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setLoading(false); return; }
      const headers = { Authorization: `Bearer ${session.access_token}` };
      // Check for at least one active listing before showing anything.
      const listingsRes = await api.get('/api/pro-listings/mine', { headers });
      const active = (listingsRes.data?.listings || []).some((l) => l.status === 'active');
      setHasActiveListing(active);
      if (!active) { setLoading(false); return; }
      const res = await api.get('/api/service-requests/available', { headers });
      setRequests(res.data?.requests || []);
      setFailed(false);
    } catch (error) {
      // A fetch error must NOT render the success empty-state ("no matching requests" would be
      // a lie on a network hiccup / rate limit). Supplementary section → self-hide instead.
      console.error('[MatchingRequestsSection] fetch failed:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (servicesEnabled) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [servicesEnabled, fetchData]);

  // Flag off → render nothing.
  if (!servicesEnabled) return null;

  // Supplementary section: while loading, render nothing rather than a titled skeleton that
  // vanishes for the majority of Pros who have no active listing (avoids the pop-in/collapse).
  if (loading) return null;

  // No active listing → nothing to answer; stay hidden. Fetch error → hide too (don't lie).
  if (!hasActiveListing || failed) return null;

  return (
    <div data-testid="matching-requests-section">
      <h2 className="font-heading text-2xl font-semibold mb-4">
        {t('services.reqMatchingTitle')}{requests.length > 0 ? ` (${requests.length})` : ''}
      </h2>

      {requests.length === 0 ? (
        <Card className="border-dashed">
          <div className="p-8 flex flex-col items-center text-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Inbox className="text-muted-foreground" size={24} aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground max-w-md">{t('services.reqMatchingEmpty')}</p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {requests.map((r) => (
            <Link
              key={r.id}
              to={`/services/requests/${r.id}`}
              className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="p-4 space-y-2 h-full active:scale-[0.99] tap-highlight">
                <p className="text-sm font-medium line-clamp-2" title={r.title}>{r.title}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {r.municipality && (
                    <span className="flex items-center gap-1">
                      <MapPin size={12} aria-hidden="true" />{r.municipality}
                    </span>
                  )}
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                    <Clock size={10} className="mr-0.5" aria-hidden="true" />
                    {t(`services.reqUrgency_${r.urgency}`)}
                  </Badge>
                </div>
                <p className="text-sm font-semibold text-primary">{budgetLabel(r, t)}</p>
                {r.expires_at && (
                  <p className="text-xs text-muted-foreground">{expiresLabel(r.expires_at, t)}</p>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchingRequestsSection;
